import { faHourglassStart } from "@fortawesome/pro-regular-svg-icons";
import { faBellSlash } from "@fortawesome/pro-regular-svg-icons/faBellSlash";
import { faCircleEllipsis } from "@fortawesome/pro-regular-svg-icons/faCircleEllipsis";
import { faShareNodes } from "@fortawesome/pro-regular-svg-icons/faShareNodes";
import { faBell } from "@fortawesome/pro-solid-svg-icons/faBell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { CorePermissions } from "app/appPermissions";
import { fetchKnowledgeQuickAccess } from "app/features/menu/menuSlice";
import { store } from "app/store";
import { MprcButton, MprcIconButton, MprcIconFa, MprcRequestErrorDialog, MprcText } from "components";
import { IconInterest, IconInterestRequired } from "components/icons";
import { AutoCompleteDataModel } from "components/MprcAutoComplete";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { MprcDropdownBuildGroupedItems, MprcDropdownItem, MprcIconButtonDropdown } from "components/MprcIconButtonDropdown";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { FeedTopicHeaderResponse } from "generated/CoreApi_v1";
import HtmlUtils from "lib/HtmlUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { DashboardPaths } from "routes/DashboardRouter";
import { FoundationPaths } from "routes/FoundationRouter";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { DeleteTopic } from "views/foundation/topic/Home/ListTopics/DeleteTopic";
import { JoinTopics } from "views/foundation/topic/Home/ListTopics/JoinTopics";

////////////////////////////////////////////////////////////////////////////////

interface SubscribeTopicProps {
  topicId: string;
  required: boolean;
  isDirectUser: boolean;
  onRefresh: () => void;
}

interface IProps {
  topic: FeedTopicHeaderResponse | null;
  onSuccess?: () => void;
}

////////////////////////////////////////////////////////////////////////////////

export const ShareTopicButton: React.FC<IProps> = (props) => {

  const { topicId } = props.topic!;

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("knowledge");

  type ShareButtonOptions = "copyLink";

  ///
  const copyLink = () => {
    const host = window.location.protocol + "//" + window.location.host;
    const topicUrl = `${host}${KnowledgePaths.FeedTopic(topicId)}`;

    HtmlUtils.copyToClipboard(topicUrl)
      .then(() => enqueueSnackbar(t("Feed.Card.Common.CopyLinkSuccessMessage"), { variant: "success" }))
      .catch(() => enqueueSnackbar(t("Feed.Card.Common.CopyLinkFailMessage"), { variant: "error" }));
  };

  ///
  const getMenuItems = (): MprcDropdownItem<ShareButtonOptions>[] => {
    var group1: MprcDropdownItem<ShareButtonOptions>[] = [
      { text: t("Feed.Card.Common.CopyLinkAction"), value: "copyLink" }
    ];

    return MprcDropdownBuildGroupedItems([group1]);
  };

  ///
  const handleMenuItem = (option: ShareButtonOptions) => {
    switch (option) {
      case "copyLink":
        copyLink();
        break;
    }
  };

  return (
    <>
      <Typography variant="iconText" component="div">
        <MprcIconButtonDropdown
          color="paper"
          icon={<FontAwesomeIcon icon={faShareNodes} fixedWidth />}
          items={getMenuItems()}
          onChange={handleMenuItem}
          separator="blank"
          title={t("Feed.Card.Common.ShareButtonTooltip")}
        />
      </Typography>
    </>
  );
};

////////////////////////////////////////////////////////////////////////////////

export const ActionTopicButton: React.FC<IProps> = (props) => {

  const {
    topicId,
    name,
    thumbnailUrl,
    allowManageUser,
    allowManageRelated,
    allowManageTopic,
    allowJoin,
    allowDelete,
    hasChallengeToAnswer,
  } = props.topic!;

  const { t } = useTranslation("knowledge");
  const navigate = useNavigate();
  const { permissionExplorer } = useSession();

  const [dialog, setDialog] = useState<any>();
  const [anchor, setAnchor] = React.useState<Element | null>(null);

  type ActionsOptions =
    | "managerUser"
    | "manageRelated"
    | "manageTopic"
    | "analysisUsers"
    | "analysisContent"
    | "challenge"
    | "join"
    | "delete"
    ;

  type MenuItem = { key: ActionsOptions, label: React.ReactNode; };

  const groupOptions: Array<Array<MenuItem>> = [];
  const groupItems0: MenuItem[] = [];
  if (hasChallengeToAnswer) groupItems0.push({ key: "challenge", label: t("Feed.Topic.Actions.Challenge") });
  if (groupItems0.length > 0) groupOptions.push(groupItems0);

  const groupItems1: MenuItem[] = [];
  if (allowManageUser) groupItems1.push({ key: "managerUser", label: t("Feed.Topic.Actions.ManageUser") });
  if (allowManageRelated) groupItems1.push({ key: "manageRelated", label: t("Feed.Topic.Actions.ManageRelated") });
  if (allowManageTopic) groupItems1.push({ key: "manageTopic", label: t("Feed.Topic.Actions.ManageTopic") });
  if (groupItems1.length > 0) groupOptions.push(groupItems1);

  const groupItems2: MenuItem[] = [];
  if (permissionExplorer.hasOne([CorePermissions.TrainingViewDashboard])) {
    groupItems2.push({ key: "analysisUsers", label: t("Feed.Topic.Actions.AnalysisUsers") });
  }
  if (permissionExplorer.hasOne([CorePermissions.KnowledgeViewDashboard])) {
    groupItems2.push({ key: "analysisContent", label: t("Feed.Topic.Actions.AnalysisContent") });
  }
  if (groupItems2.length > 0) groupOptions.push(groupItems2);

  const groupItems3: MenuItem[] = [];
  if (allowJoin) groupItems3.push({ key: "join", label: t("Feed.Topic.Actions.Join") });
  if (allowDelete) groupItems3.push({ key: "delete", label: <MprcText color="error">{t("Feed.Topic.Actions.Delete")}</MprcText> });
  if (groupItems3.length > 0) groupOptions.push(groupItems3);

  // Opções do menu
  const menuItems = useMemo(() => {
    var arr: React.ReactNode[] = [];

    groupOptions.forEach((g, gindex) => {
      g.forEach((v) => {
        arr.push(<MenuItem
          key={v.key}
          onClick={
            () => {
              handleMenuItem(v.key);
            }
          }
        >
          {v.label}
        </MenuItem>);
      });

      if (gindex < groupOptions.length - 1)
        arr.push(<Box sx={{ mt: 1.5 }} key={`${gindex}_divider`} />);
    });

    return arr;
  }, [groupOptions]);

  ///
  const handleMenuItem = (option: ActionsOptions) => {

    setAnchor(null);

    switch (option) {
      case "managerUser":
        navigate(FoundationPaths.TopicList, {
          state: {
            openForm: {
              topicId,
              parentId: null,
              name,
              formToGo: "users"
            }
          }
        });
        break;

      case "manageRelated":
        navigate(FoundationPaths.TopicList, {
          state: {
            openForm: {
              topicId,
              parentId: null,
              name,
              formToGo: "related"
            }
          }
        });
        break;

      case "manageTopic":
        navigate(FoundationPaths.TopicList, {
          state: {
            openForm: {
              topicId,
              parentId: null,
              name,
              formToGo: "edit"
            }
          }
        });
        break;
      case "join":
        setDialog(
          <JoinTopics
            topicId={topicId}
            name={name}
            onClose={(topicDestinyId?: string) => {
              if (topicDestinyId) {
                navigate(KnowledgePaths.FeedTopic(topicDestinyId));
              }
              setDialog(null);
            }}
          />);
        break;
      case "analysisContent":
        navigate(DashboardPaths.KnowledgePanel, { state: { topic: { id: topicId, name: name } } });
        break;
      case "analysisUsers":
        navigate(DashboardPaths.ChallengePanel, { state: { topic: { key: topicId, label: name, thumbnailUrl: thumbnailUrl } as AutoCompleteDataModel } });
        break;
      case "delete":
        setDialog(
          <DeleteTopic
            topicId={topicId}
            name={name}
            onClose={(success) => {
              if (success) navigate(KnowledgePaths.FeedStartup);
              setDialog(null);
            }}
          />
        );
        break;
    }
  };

  return (
    <>
      {dialog}
      {menuItems.length > 0
        ? <MprcFeedTypography variant="action" color="white">
          <IconButton color="inherit" sx={{ pl: 0, pr: 0 }} onClick={(e) => setAnchor(e.currentTarget)}>
            <FontAwesomeIcon icon={faCircleEllipsis} fixedWidth />
          </IconButton>
          <Menu
            anchorEl={anchor}
            open={anchor != null}
            onClose={() => setAnchor(null)}
          >
            {menuItems}
          </Menu>
        </MprcFeedTypography>
        : null
      }
    </>
  );
};

////////////////////////////////////////////////////////////////////////////////

export const SubscribeTopicButton: React.FC<SubscribeTopicProps> = (props) => {

  const { topicId, required, onRefresh } = props;

  const api = useCoreApi();
  const { t } = useTranslation("knowledge");
  const { enqueueSnackbar } = useSnackbar();

  const [posting, setPosting] = useState<boolean>(false);
  const [viewError, setViewError] = useState<any>(null);

  ///
  const doSubscribe = async () => {
    setPosting(true);
    try {
      var response = await api.v1.knowledgeSubcription.subscribeTopic({ topicId: topicId });
      if (response.data) {
        enqueueSnackbar(t("Subcriptions.Signup.Topic.SubscribeSuccessMessage"), { variant: "success" });
        store.dispatch(fetchKnowledgeQuickAccess({}));
      }
      else {
        enqueueSnackbar(t("Error.PostFailed", { ns: "shared" }), { variant: "error" });
      }
      onRefresh();
    }
    catch (e) {
      MprcRequestSnackbarErrorReport(e);
    }
    finally {
      setPosting(false);
    }
  };

  ///
  const handleClick = () => {
    doSubscribe();
  };

  return (
    <>
      <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

      <MprcButton
        text={t("Feed.Topic.SubscribeButtonText")}
        color={"primary"}
        variant="contained"
        fullWidth
        disabled={posting}
        startIcon={required ? <IconInterestRequired /> : <IconInterest variant="regular" />}
        onClick={handleClick}
        sx={{ cursor: "pointer", "&.Mui-disabled": { color: "unset" } }}
      />
    </>
  );
};

////////////////////////////////////////////////////////////////////////////////

export const UnsubscribeTopicButton: React.FC<SubscribeTopicProps> = (props) => {

  const { topicId, required, isDirectUser, onRefresh } = props;

  const api = useCoreApi();
  const { t } = useTranslation("knowledge");
  const { enqueueSnackbar } = useSnackbar();

  const [posting, setPosting] = useState<boolean>(false);
  const [viewError, setViewError] = useState<any>(null);

  ///
  const doUnsubscribe = async () => {
    setPosting(true);
    try {
      var response = await api.v1.knowledgeSubcription.unsubscribeTopic({ topicId });
      if (response.data) {
        enqueueSnackbar(t("Subcriptions.Signup.Topic.UnsubscribeSuccessMessage"), { variant: "success" });
        store.dispatch(fetchKnowledgeQuickAccess({}));
      }
      else {
        enqueueSnackbar(t("Error.PostFailed", { ns: "shared" }), { variant: "error" });
      }
      onRefresh();

    }
    catch (e) {
      setViewError(e);
    }
    finally {
      setPosting(false);
    }
  };

  ///
  const handleClick = () => {
    if (!isDirectUser) {
      enqueueSnackbar(t("Feed.Topic.CannotUnsubscribeIndirectUserMessage", { variant: "error" }));
    }
    else {
      doUnsubscribe();
    }
  };

  return (
    <>
      <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

      <MprcButton
        text={t("Feed.Topic.FollowingButtonText")}
        color={"success"}
        variant="contained"
        fullWidth
        disabled={posting}
        startIcon={required ? <IconInterestRequired /> : <IconInterest variant="solid" />}
        onClick={handleClick}
        sx={{ cursor: "pointer", "&.Mui-disabled": { color: "unset" } }}
      />
    </>
  );
};

export const PendingSubscriptionTopicButton = () => {
  const { t } = useTranslation("knowledge");
  return <MprcButton
    text={t("Feed.Topic.InterestPendingText")}
    variant="contained"
    fullWidth
    disabled={true}
    startIcon={<FontAwesomeIcon icon={faHourglassStart} />}
    sx={{ "&.Mui-disabled": { color: "unset" } }}
  />;
};

export const ToggleMutedTopicButton: React.FC<IProps> = (props) => {

  const { topic, onSuccess } = props;

  const { t } = useTranslation("knowledge");
  const api = useCoreApi();
  const { enqueueSnackbar } = useSnackbar();

  const [posting, setPosting] = useState<boolean>(false);
  const [viewError, setViewError] = useState<any>(null);

  ///
  const doToggleMuted = async () => {
    setPosting(true);
    try {
      var result = await api.v1.knowledgeSubcription.toggleMutedTopic({ topicId: topic!.topicId });

      if (result) {
        if (topic!.muted) {
          enqueueSnackbar(t("Feed.Topic.MutedMessage"), { variant: "success" });
        }
        else {
          enqueueSnackbar(t("Feed.Topic.UnmutedMessage"), { variant: "success" });
        }
      }

      if (onSuccess) onSuccess();
    }
    catch (e) {
      setViewError(e);
    }
    finally {
      setPosting(false);
    }
  };

  const handleClick = () => {
    doToggleMuted();
  };

  return (
    <>
      <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

      <MprcIconButton
        title={topic!.muted ? t("Feed.Topic.UnmutedButtonTitle") : t("Feed.Topic.MutedButtonTitle")}
        color={topic!.muted ? "muted" : "success"}
        disabled={posting}
        onClick={handleClick}
      >
        <MprcIconFa {...props} icon={topic!.muted ? faBellSlash : faBell} />
      </MprcIconButton>
    </>
  );
};
